import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { AssignmentDto, FillSheetDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─── ONE DUTY'S FILL SHEET, AND THE ONLY WAY ITS SLOTS ARE WRITTEN ───────────────
 *
 * ⚠️ [D4.3] EXTRACTED FROM `pages/DutyFill` SO THERE IS ONE SWAP PATH, NOT TWO.
 *
 * The arrangement detail page grew an officer surface that also has to Replace and Remove.
 * Copying these three mutations would have produced a SECOND definition of when a swap is
 * allowed and what happens after one — and the two would diverge, which is the whole reason
 * the brief says not to write a second swap path. Both screens now call this.
 *
 * Everything P4 recorded about the hot path is preserved, and it is preserved MECHANICALLY
 * rather than by being remembered:
 *
 * ⚠️ THE HOT PATH INVALIDATES NOTHING. P3b.3 lost data because a mutation invalidated
 * `queryKeys.arrangements.all`, a PREFIX of the record being edited, so the record refetched
 * and a hydration effect overwrote what was being typed. A fill screen takes up to twelve
 * writes in ninety seconds. `setQueryData` neither refetches nor marks stale, so there is no
 * refetch for a local write to race — the mechanism is removed rather than an instance of it.
 *
 * ⚠️ THE ROLLBACK IS ONE SLOT, NEVER THE SHEET. React Query's canonical `onMutate`
 * snapshot-and-restore would undo the eleven other writes still in flight.
 *
 * ⚠️ NOTHING HERE MAY INVALIDATE `queryKeys.fill.all` OR `fill.sheets()` — both are prefixes
 * of a live sheet. `lib/fillKeyIsolation.test.ts` reads every production file and fails on it,
 * so a future edit to this file is covered without anyone remembering the rule.
 */
export type FillSheetController = {
  sheet: ReturnType<typeof useQuery<FillSheetDto>>;
  data: FillSheetDto | undefined;
  /** Applies ONE slot to the cache. Never replaces the sheet. */
  putSlot: (slotRef: string, assignment: AssignmentDto | null) => void;
  /** Re-reads and writes the whole sheet. For an ERROR path only — never a success one. */
  refreshSheet: () => Promise<void>;
  /** Marks the roll-up counts stale, to be flushed once on unmount. */
  markDirty: () => void;
  flushCounts: () => void;
  assign: ReturnType<typeof useMutation<AssignmentDto, ApiError, { slotRef: string; officerId: string }>>;
  swap: ReturnType<
    typeof useMutation<AssignmentDto, ApiError, { assignmentId: string; officerId: string; reason?: string }>
  >;
  remove: ReturnType<typeof useMutation<AssignmentDto, ApiError, { slotRef: string; assignmentId: string }>>;
};

export const useFillSheet = (
  dutyId: string,
  options?: {
    enabled?: boolean;
    /**
     * ⚠️ [C1a §3] Called when a per-slot write is rolled back, so a caller holding an
     * OPTIMISTIC set can drop the same slot. `DutyFill` is the one consumer; the arrangement
     * detail surface keeps a purely derived set and passes nothing.
     */
    onSlotRolledBack?: (slotRef: string) => void;
    /**
     * ⚠️ [D4.5 §3e] ONE DIVISION'S SHARE OF THIS DUTY, WHEN THE CALLER ARRIVED ASKING FOR ONE.
     *
     * The inbox row is now per division per duty point per night, and two rows can name the
     * same deployment — `CD · DP1` and `ND · DP1`. Without this both links open the same
     * sheet and `Mark complete` closes a division the operator was not looking at.
     *
     * It narrows the SHEET as well as the button, on the server: everything downstream —
     * lines, slots, dropped rows, the totals — derives from the share set.
     */
    division?: string;
  },
): FillSheetController => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  /** Set by every write; flushed once on the way out. Never a dependency of a render. */
  const countsDirty = useRef(false);

  const division = options?.division;
  const onSlotRolledBack = options?.onSlotRolledBack;
  const sheetKey = queryKeys.fill.sheet(dutyId, division);

  const sheet = useQuery({
    queryKey: sheetKey,
    queryFn: ({ signal }) =>
      api.get<FillSheetDto>(`/duties/${dutyId}/fill`, { ...(division ? { query: { division } } : {}), signal }),
    enabled: dutyId !== '' && (options?.enabled ?? true),
    /*
     * ⚠️ BOTH LINES ARE LOAD-BEARING UNDER TEST EVEN THOUGH `queryClient.ts` ALREADY SETS
     * THEM. `test/renderWithProviders.tsx` builds its OWN client with `{retry:false,
     * gcTime:0}` and NOT the app defaults — so under test `staleTime` is 0 and
     * `refetchOnWindowFocus` is on. A background refetch is the last door through which a
     * server copy could overwrite staged local state.
     */
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const arrangementId = sheet.data?.duty.arrangementId ?? '';

  const putSlot = useCallback(
    (slotRef: string, assignment: AssignmentDto | null): void => {
      queryClient.setQueryData<FillSheetDto>(sheetKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          lines: current.lines.map((line) => ({
            ...line,
            allocations: line.allocations.map((allocation) => ({
              ...allocation,
              slots: allocation.slots.map((slot) => (slot.slotRef === slotRef ? { ...slot, assignment } : slot)),
            })),
          })),
        };
      });
    },
    [queryClient, sheetKey],
  );

  /**
   * ⚠️ [amended PRE-P6.2] IT CARRIES THE DIVISION NOW, AND ITS ABSENCE WAS A BUG.
   *
   * `sheetKey` includes `?division=` when the inbox row asked for one ([D4.5] §3e), and this
   * fetch did not — so on a narrowed sheet it read the WHOLE duty and wrote it into the
   * narrowed key. A super admin who arrived from `CD · DP1` and hit any error path would
   * silently start seeing every division's lines under a heading that says one. Latent since
   * §3e, because the only callers were error paths; §2's completion refresh below makes it a
   * SUCCESS path, which is what turned it into a defect worth finding.
   */
  const refreshSheet = useCallback(async (): Promise<void> => {
    const fresh = await api.get<FillSheetDto>(`/duties/${dutyId}/fill`, {
      ...(division ? { query: { division } } : {}),
    });
    queryClient.setQueryData<FillSheetDto>(sheetKey, fresh);
  }, [dutyId, division, queryClient, sheetKey]);

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [PRE-P6.2] THE ONE RE-READ ON THE HOT PATH, AND IT FIRES AT MOST ONCE PER SHEET.
   *
   *  `autoCompleteFullShares` closes a share SERVER-SIDE the moment its last slot is named,
   *  and this screen never learned: `putSlot` patches ONE slot and nothing else, by design,
   *  so `fillState` stayed `PARTIAL`, the footer pill stayed amber and `Mark complete` stayed
   *  enabled on a duty the server had already completed. Pressing it then 409s — "already
   *  closed" — which reads as the button being broken.
   *
   *  ⚠️ IT IS NOT AN INVALIDATION, and that distinction is the whole of P4's rule. Nothing is
   *  marked stale and no refetch is scheduled that a local write could race; this is one
   *  explicit read whose result is written with `setQueryData`, at a boundary that happens
   *  once. `lib/fillKeyIsolation.test.ts` reads every production file for invalidations at or
   *  above a live sheet and this adds none.
   *
   *  ⚠️ IT RUNS IN `onSuccess`, SO THE LAST WRITE TO LAND IS THE ONE THAT SEES A FULL SHEET.
   *  Twelve names in flight resolve in any order; each patches its own slot and then asks
   *  whether the CACHE is now complete. Eleven of them answer no. No counter, no ref, and no
   *  way for the check to run before the data it is checking.
   *
   *  ⚠️ A STAGED PROPOSAL SUPPRESSES IT STRUCTURALLY, WITH NO CHECK FOR IT. `refreshSheet`
   *  replaces the whole sheet, which P4 names as the last door through which a server copy
   *  could overwrite staged local state. There is no guard because none is needed: a staged
   *  name is not in the cached sheet's assignments, so the slot reads EMPTY here and the
   *  early return below fires. The safety is a property of what the cache holds rather than
   *  a condition somebody has to remember.
   *
   *  ⚠️ THE FILL DIRECTION ONLY, DELIBERATELY. Removing a name from a completed share is the
   *  mirror case and is NOT re-read here, because "every slot named" and "fillState is
   *  COMPLETE" are not the same claim — an explicit completion at partial (FR-5.3.6, with a
   *  shortfall reason) is legitimately COMPLETE with empty slots, so "COMPLETE but a slot is
   *  empty" cannot be treated as stale. Unchanged behaviour, named rather than assumed.
   *
   *  ⚠️ [amended C5b review] STILL TRUE OF THIS FUNCTION, AND NO LONGER TRUE OF THE REMOVAL. [C5b
   *  §8a] made the server CLEAR an AUTOMATIC lock whose arithmetic fails, so a removal from a full
   *  share now reopens it — and this screen went on showing `🔒 All Filled` over the server's open
   *  share, with `Complete with short-fall` refused as "already complete" until a reload.
   *  `refreshWhenLockCleared` below is the removal's own boundary; a DECLARED close is still never
   *  reopened, and is exactly what that function's condition leaves out.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const refreshWhenComplete = useCallback((): void => {
    const current = queryClient.getQueryData<FillSheetDto>(sheetKey);
    if (!current) return;

    const slots = current.lines.flatMap((line) => line.allocations.flatMap((allocation) => allocation.slots));
    if (slots.length === 0 || slots.some((slot) => !slot.assignment?.officer)) return;

    void refreshSheet();
  }, [queryClient, sheetKey, refreshSheet]);

  /**
   * ⚠️ [C5b review] THE REMOVAL'S ONE RE-READ — ONLY WHEN THE SHEET HOLDS AN AUTOMATIC LOCK.
   *
   * `recountShares` clears a lock that `autoCompleteFullShares` wrote (a `completedAt` with no
   * `shortfallReason`) once a name comes off, and `putSlot` below cannot know that: it patches one
   * slot. So the removal of a name re-reads the sheet exactly when the cache holds such a lock, and
   * never otherwise — an open share has nothing to reopen, and a DECLARED close ([C3c.2], a reason)
   * is somebody's decision the server never touches, so neither costs a read.
   *
   * ⚠️ NOT AN INVALIDATION, for `refreshWhenComplete`'s reason above: one explicit read written with
   * `setQueryData`, at a boundary that happens once per removal. A staged name lives in the page's
   * own state rather than in this cache, so the whole-sheet write cannot overwrite it.
   */
  const refreshWhenLockCleared = useCallback((): void => {
    const current = queryClient.getQueryData<FillSheetDto>(sheetKey);
    if (!current) return;
    if (!current.shares.some((share) => share.completedAt !== null && share.shortfallReason === null)) return;
    void refreshSheet();
  }, [queryClient, sheetKey, refreshSheet]);

  const markDirty = useCallback((): void => {
    countsDirty.current = true;
  }, []);

  /**
   * ⚠️ THE ONLY INVALIDATION HERE, AND IT RUNS AT MOST ONCE PER VISIT.
   *
   * `arrangements.lists()` and a NAMED `detail(id)` — never `arrangements.all`, which is a
   * prefix of `detail`, of `deployments` and of `publishPreview`. `namedCount` changed on a
   * list row and on one order; nothing else needs to know.
   */
  const flushCounts = useCallback((): void => {
    if (!countsDirty.current) return;
    countsDirty.current = false;
    void queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.lists() });
    if (arrangementId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.detail(arrangementId) });
    }
  }, [queryClient, arrangementId]);

  useEffect(() => flushCounts, [flushCounts]);

  const assign = useMutation({
    mutationFn: (input: { slotRef: string; officerId: string }) =>
      api.post<AssignmentDto>(`/duties/${dutyId}/assignments`, input),
    onSuccess: (saved) => {
      putSlot(saved.slotRef, saved);
      countsDirty.current = true;
      // ⚠️ [PRE-P6.2] The last name to land re-reads once, so the footer and `Mark complete`
      // agree with the share the server has already closed. See `refreshWhenComplete`.
      refreshWhenComplete();
    },
    onError: (error: ApiError, input) => {
      // ⚠️ ONE SLOT BACK, never a snapshot of the sheet — see the header.
      putSlot(input.slotRef, null);
      /*
       * ⚠️ [C1a §3] AND THE CALLER'S OPTIMISTIC SET IS ROLLED BACK WITH IT. `DutyFill` greys
       * an officer the moment it names them, before the write answers; a refused write left
       * them greyed for the life of the page, so the operator could not retry the very
       * officer the failure was about. The cached slot and the local set are one rollback.
       */
      onSlotRolledBack?.(input.slotRef);
      void message.error(error.message);
    },
    // ⚠️ NO `onSettled` INVALIDATION. The three lines everybody reaches for are the bug.
  });

  const swap = useMutation({
    mutationFn: (input: { assignmentId: string; officerId: string; reason?: string }) =>
      api.post<AssignmentDto>(`/duties/${dutyId}/assignments/${input.assignmentId}/swap`, {
        officerId: input.officerId,
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: (saved) => {
      putSlot(saved.slotRef, saved);
      countsDirty.current = true;
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      void refreshSheet();
    },
  });

  const remove = useMutation({
    mutationFn: (input: { slotRef: string; assignmentId: string }) =>
      api.delete<AssignmentDto>(`/duties/${dutyId}/assignments/${input.assignmentId}`),
    onSuccess: (saved) => {
      putSlot(saved.slotRef, saved);
      countsDirty.current = true;
      // ⚠️ [C5b review] The server may have reopened an automatic lock. See `refreshWhenLockCleared`.
      refreshWhenLockCleared();
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      void refreshSheet();
    },
  });

  return { sheet, data: sheet.data, putSlot, refreshSheet, markDirty, flushCounts, assign, swap, remove };
};
